import { useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, Mail, Pencil, Send } from "lucide-react";
import api from "@/lib/api";
import { TeamCopyNote } from "@/components/TeamCopyNote";

const inputCls = "h-10 w-full bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none";
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const NOTE_SNIPPETS = [
  { key: "bza", label: "BzA liegt vor", text: "Die Bestätigung zum Antrag (BzA) liegt uns bereits vor." },
  { key: "termin", label: "Termin abgestimmt", text: "Der Montagetermin wurde bereits mit Ihnen abgestimmt." },
  { key: "unterlagen", label: "Unterlagen fehlen", text: "Bitte senden Sie uns noch die fehlenden Unterlagen (z. B. Fotos, Zählernummer) per E-Mail zu." },
];

// Auftragsbestätigung (PDF) senden: Kunden-Adresse bestätigen oder andere eingeben.
export function OrderConfirmationSend({ offerId, projectId, customerEmail, onSent }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(!customerEmail);
  const [email, setEmail] = useState("");
  const [delivery, setDelivery] = useState("");
  const [note, setNote] = useState("");
  const addSnippet = (text) => setNote((n) => (n.includes(text) ? n : (n.trim() ? `${n.trim()}\n${text}` : text)));
  const [sending, setSending] = useState(false);
  const target = custom ? email.trim() : customerEmail;

  const send = async () => {
    if (!isEmail(target)) return toast.error("Bitte eine gültige E-Mail-Adresse eingeben");
    setSending(true);
    try {
      const body = { recipient_email: target };
      if (delivery) body.estimated_delivery_iso = delivery;
      if (note.trim()) body.note = note.trim();
      const r = await api.post(`/offers/${offerId}/order-confirmation/send`, body);
      if (r.data?.ok) {
        const n = r.data?.log?.team_bcc?.length || 0;
        toast.success(`Auftragsbestätigung an ${target} versendet${n ? ` · Kopie an ${n} Team-Mitglied${n === 1 ? "" : "er"}` : ""}`);
        setOpen(false);
        onSent?.();
      } else {
        toast.warning(`PDF erstellt, Versand fehlgeschlagen: ${r.data?.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-zinc-800/80" data-testid="order-confirmation-block">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
          <ClipboardCheck size={13} className="text-[#00E575]" /> Auftragsbestätigung (PDF)
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="open-order-confirmation"
            className="w-full sm:w-auto min-h-10 py-2 px-3.5 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] leading-tight hover:bg-[#00C964] active:scale-95 transition inline-flex items-center justify-center gap-1.5">
            <Mail size={14} /> Auftragsbestätigung versenden
          </button>
        )}
      </div>
      {open && (
        <div className="bg-[#09090B] border border-[#00E575]/40 rounded-xl p-3 space-y-3" data-testid="order-confirmation-panel">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-zinc-400">Voraussichtlicher Liefertermin (optional, erscheint als KW)</span>
            <input type="date" lang="de-DE" value={delivery} onChange={(e) => setDelivery(e.target.value)}
              data-testid="order-confirmation-delivery-input" className={inputCls} />
          </label>
          <div className="space-y-1.5">
            <span className="text-[12px] text-zinc-400">Notiz an den Kunden (optional, erscheint in E-Mail und PDF)</span>
            <div className="flex flex-wrap gap-1.5">
              {NOTE_SNIPPETS.map((s) => (
                <button key={s.key} type="button" onClick={() => addSnippet(s.text)} data-testid={`order-confirmation-snippet-${s.key}`}
                  className="h-8 px-2.5 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-200 text-[12px] hover:border-[#00E575] active:scale-95 transition">
                  + {s.label}
                </button>
              ))}
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1500))} rows={3}
              placeholder="z. B. BzA liegt bereits vor, Besonderheiten zum Projekt …" data-testid="order-confirmation-note-input"
              className="w-full bg-[#18181B] border border-zinc-700 rounded-lg px-3 py-2 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none resize-y" />
            <div className="text-[11px] text-zinc-500 text-right">{note.length}/1500</div>
          </div>
          <div className="space-y-2">
            <div className="text-[12px] text-zinc-400">Empfänger</div>
            {customerEmail && !custom ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg bg-[#18181B] border border-zinc-700 px-3 py-2">
                <span className="text-[13.5px] text-zinc-50 font-semibold break-words min-w-0" data-testid="order-confirmation-customer-email">{customerEmail}</span>
                <button onClick={() => setCustom(true)} data-testid="order-confirmation-other-email"
                  className="h-8 px-2.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[12px] inline-flex items-center gap-1 self-start sm:self-auto shrink-0">
                  <Pencil size={12} /> Andere Adresse
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <input type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@beispiel.de" data-testid="order-confirmation-email-input" className={inputCls} />
                {customerEmail && (
                  <button onClick={() => { setCustom(false); setEmail(""); }} data-testid="order-confirmation-use-customer-email"
                    className="text-[12px] text-[#00E575] hover:underline">Kunden-Adresse {customerEmail} verwenden</button>
                )}
                {!customerEmail && <div className="text-[11.5px] text-amber-300">Beim Kunden ist keine E-Mail hinterlegt.</div>}
              </div>
            )}
          </div>
          <TeamCopyNote projectId={projectId} />
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={send} disabled={sending || !target} data-testid="order-confirmation-send-btn"
              className="w-full sm:flex-1 min-h-10 py-2 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] leading-tight hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 text-center">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} className="shrink-0" /> {custom ? "Senden" : "Bestätigen & senden"}</>}
            </button>
            <button onClick={() => setOpen(false)} disabled={sending} data-testid="order-confirmation-cancel"
              className="w-full sm:w-auto h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px]">Abbrechen</button>
          </div>
          <p className="text-[11px] text-zinc-500">Nach dem Versand springt das Projekt automatisch auf „Auftragsbestätigung versendet“.</p>
        </div>
      )}
    </div>
  );
}
